import React, { useEffect, useState } from 'react';
import { getNotificationTemplates, updateNotificationTemplate, resetNotificationTemplate, sendCustomNotification, getUsers } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Bell, RotateCcw, Send, Save, User } from 'lucide-react';

const TAB_CUSTOM = 'custom';

const Notifications = () => {
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);

  // Custom notification state
  const [customTitle, setCustomTitle] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [target, setTarget] = useState('all');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchUsers();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await getNotificationTemplates();
      setTemplates(res.data);
      if (!activeTab && res.data.length > 0) {
        setActiveTab(res.data[0].key);
        setEditTitle(res.data[0].title);
        setEditBody(res.data[0].body);
      }
    } catch { toast.error('Erro ao carregar templates'); }
  };

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data.filter(u => u.role === 'client' || u.role === 'provider'));
    } catch { /* optional */ }
  };

  const switchTab = (key) => {
    setActiveTab(key);
    if (key !== TAB_CUSTOM) {
      const t = templates.find(t => t.key === key);
      if (t) { setEditTitle(t.title); setEditBody(t.body); }
    }
  };

  const selectedTemplate = templates.find(t => t.key === activeTab);

  const handleSave = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      await updateNotificationTemplate(selectedTemplate.key, { title: editTitle, body: editBody });
      toast.success('Template salvo com sucesso!');
      const res = await getNotificationTemplates();
      setTemplates(res.data);
    } catch { toast.error('Erro ao salvar template'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!selectedTemplate) return;
    try {
      await resetNotificationTemplate(selectedTemplate.key);
      toast.success('Template restaurado ao padrão!');
      const res = await getNotificationTemplates();
      setTemplates(res.data);
      const updated = res.data.find(t => t.key === selectedTemplate.key);
      if (updated) { setEditTitle(updated.title); setEditBody(updated.body); }
    } catch { toast.error('Erro ao restaurar template'); }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!customTitle.trim() || !customBody.trim()) {
      toast.error('Preencha título e corpo da mensagem');
      return;
    }
    const finalTarget = target === 'specific' ? selectedUserId : target;
    if (target === 'specific' && !selectedUserId) {
      toast.error('Selecione um usuário');
      return;
    }
    setSending(true);
    try {
      const res = await sendCustomNotification({ title: customTitle, body: customBody, target: finalTarget });
      toast.success(res.data.message);
      setCustomTitle('');
      setCustomBody('');
    } catch { toast.error('Erro ao enviar notificação'); }
    finally { setSending(false); }
  };

  const insertVariable = (variable) => {
    setEditBody(prev => prev + variable);
  };

  const tabs = [
    ...templates.map(t => ({ key: t.key, label: t.label })),
    { key: TAB_CUSTOM, label: 'Notificação Personalizada' }
  ];

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <h1 className="text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900">
          Notificações
        </h1>
        <p className="text-slate-600 font-primary mt-1">Personalize mensagens e envie notificações</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
        {/* Tabs */}
        <div className="flex border-b border-slate-200" data-testid="notification-tabs">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => switchTab(tab.key)}
              className={`flex-1 px-4 py-3 text-sm font-secondary font-semibold uppercase tracking-wider transition-colors ${
                activeTab === tab.key
                  ? 'text-slate-900 border-b-2 border-slate-900 bg-slate-50'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {/* Template Editor */}
          {activeTab !== TAB_CUSTOM && selectedTemplate && (
            <div className="space-y-5" data-testid="template-editor">
              {/* Recipient */}
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-sm border border-slate-100">
                <User className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600">
                  Destinatário: <strong className="text-slate-900">{selectedTemplate.recipient_label}</strong>
                </span>
                {selectedTemplate.is_custom && (
                  <span className="ml-auto text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-sm">Personalizado</span>
                )}
              </div>

              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Título</Label>
                <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                  className="mt-1 rounded-sm" data-testid="template-title-input" />
              </div>

              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Corpo da Mensagem</Label>
                <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)}
                  rows={3} className="mt-1 rounded-sm" data-testid="template-body-input" />
              </div>

              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">Variáveis Disponíveis</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate.variables?.map(v => (
                    <button key={v} type="button" onClick={() => insertVariable(v)}
                      className="px-2.5 py-1 text-xs font-mono bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm border border-slate-200 transition-colors cursor-pointer">
                      {v}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">Clique para inserir no corpo da mensagem</p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                  data-testid="save-template-btn">
                  <Save className="h-4 w-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button onClick={handleReset} variant="outline" className="rounded-sm"
                  data-testid="reset-template-btn">
                  <RotateCcw className="h-4 w-4 mr-2" /> Restaurar Padrão
                </Button>
              </div>
            </div>
          )}

          {/* Custom Notification */}
          {activeTab === TAB_CUSTOM && (
            <form onSubmit={handleSendNotification} className="space-y-4" data-testid="custom-notification">
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Destinatário</Label>
                <select value={target} onChange={(e) => { setTarget(e.target.value); setSelectedUserId(''); }}
                  className="mt-1 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="notification-target-select">
                  <option value="all">Todos (Clientes + Fornecedores)</option>
                  <option value="all_clients">Todos os Clientes</option>
                  <option value="all_providers">Todos os Fornecedores</option>
                  <option value="specific">Usuário Específico</option>
                </select>
              </div>

              {target === 'specific' && (
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Selecionar Usuário</Label>
                  <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}
                    className="mt-1 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                    data-testid="notification-user-select">
                    <option value="">Selecione...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role === 'client' ? 'Cliente' : 'Fornecedor'}) - {u.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Título</Label>
                <Input value={customTitle} onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="Título da notificação" required className="mt-1 rounded-sm"
                  data-testid="custom-notification-title" />
              </div>

              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Mensagem</Label>
                <Textarea value={customBody} onChange={(e) => setCustomBody(e.target.value)}
                  placeholder="Corpo da notificação" required rows={3} className="mt-1 rounded-sm"
                  data-testid="custom-notification-body" />
              </div>

              <Button type="submit" disabled={sending}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                data-testid="send-notification-btn">
                <Send className="h-4 w-4 mr-2" /> {sending ? 'Enviando...' : 'Enviar Notificação'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
